// components/patient/PrescriptionDetail.jsx
import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Icon } from '../ui/Icon';
import { useBrand } from '../../contexts/BrandContext';
import { DB } from '../../utils/db';

export function PrescriptionDetail({ prescriptionId, onNavigate }) {
  const { brand } = useBrand();
  const [prescription, setPrescription] = useState(null);
  const [relatedOrders, setRelatedOrders] = useState([]);
  
  useEffect(() => {
    loadData();
  }, [prescriptionId]);
  
  const loadData = async () => {
    const allRxs = await DB.get('d2p_prescriptions') || [];
    const rx = allRxs.find(r => r.id === prescriptionId);
    setPrescription(rx);
    
    if (rx) {
      const allOrders = await DB.get('d2p_orders') || [];
      const related = allOrders.filter(o => 
        o.prescriptionIds && o.prescriptionIds.includes(rx.id)
      );
      setRelatedOrders(related);
    }
  };
  
  if (!prescription) {
    return (
      <div style={{
        background: '#F9FAFB',
        minHeight: '100vh',
        padding: '40px 24px',
        fontFamily: 'Libre Franklin, sans-serif'
      }}>
        <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
          <p>Prescription not found</p>
          <Button onClick={() => onNavigate('prescriptions')}>
            Back to Prescriptions
          </Button>
        </div>
      </div>
    );
  }
  
  const timeline = [
    { status: 'Uploaded', date: prescription.uploadedAt, completed: true },
    { status: 'Under Review', date: prescription.reviewedAt, completed: !!prescription.reviewedAt },
    { status: 'Verified', date: prescription.verifiedAt, completed: prescription.status === 'verified' }
  ];
  
  return (
    <div style={{
      background: '#F9FAFB',
      minHeight: '100vh',
      padding: '40px 24px',
      fontFamily: 'Libre Franklin, sans-serif'
    }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <Button
          variant="ghost"
          icon="arrowLeft"
          onClick={() => onNavigate('prescriptions')}
          style={{ marginBottom: 24 }}
        >
          Back to Prescriptions
        </Button>
        
        {/* Header Card */}
        <Card style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', gap: 20, alignItems: 'start', marginBottom: 24 }}>
            <div style={{
              width: 72,
              height: 72,
              borderRadius: 16,
              background: `${brand.c.accent}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Icon name="clipboard" size={36} color={brand.c.accent} />
            </div>
            
            <div style={{ flex: 1 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 12,
                flexWrap: 'wrap'
              }}>
                <h1 style={{
                  fontSize: 28,
                  fontWeight: 700,
                  color: brand.c.navy,
                  margin: 0
                }}>
                  {prescription.medication}
                </h1>
                <Badge status={prescription.status} />
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 16,
                fontSize: 14,
                color: '#6B7280'
              }}>
                <div>
                  <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                    Prescription ID
                  </div>
                  <div style={{ fontFamily: 'monospace' }}>{prescription.id}</div>
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                    Uploaded
                  </div>
                  <div>{new Date(prescription.uploadedAt).toLocaleString()}</div>
                </div>
                {prescription.refills !== undefined && (
                  <div>
                    <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                      Refills Remaining
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: brand.c.accent }}>
                      {prescription.refills}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {prescription.status === 'verified' && prescription.refills > 0 && (
            <div style={{ display: 'flex', gap: 12 }}>
              <Button icon="package" onClick={() => onNavigate('bundle')}>
                Order This Prescription
              </Button>
              <Button variant="secondary" icon="msgCircle" onClick={() => onNavigate('messages')}>
                Message Pharmacist
              </Button>
            </div>
          )}
        </Card>
        
        {/* Timeline */}
        <Card style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 24, color: brand.c.navy }}>
            Status Timeline
          </h2>
          
          <div style={{ position: 'relative' }}>
            {timeline.map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 16, marginBottom: i < timeline.length - 1 ? 32 : 0 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: item.completed ? brand.c.accent : '#E5E7EB',
                    color: item.completed ? 'white' : '#9CA3AF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    zIndex: 1
                  }}>
                    {item.completed ? (
                      <Icon name="check" size={20} />
                    ) : (
                      <div style={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        background: '#9CA3AF'
                      }} />
                    )}
                  </div>
                  {i < timeline.length - 1 && (
                    <div style={{
                      width: 2,
                      flex: 1,
                      background: item.completed ? brand.c.accent : '#E5E7EB',
                      marginTop: 8,
                      marginBottom: 8
                    }} />
                  )}
                </div>
                
                <div style={{ flex: 1, paddingTop: 8 }}>
                  <div style={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: item.completed ? brand.c.navy : '#9CA3AF',
                    marginBottom: 4
                  }}>
                    {item.status}
                  </div>
                  {item.date && (
                    <div style={{ fontSize: 14, color: '#6B7280' }}>
                      {new Date(item.date).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
        
        {/* Prescription Details */}
        <Card style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 24, color: brand.c.navy }}>
            Prescription Details
          </h2>
          
          <div style={{ display: 'grid', gap: 20 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#6B7280', marginBottom: 8 }}>
                Medication
              </div>
              <div style={{ fontSize: 16, color: brand.c.navy }}>
                {prescription.medication}
              </div>
            </div>
            
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#6B7280', marginBottom: 8 }}>
                Prescriber
              </div>
              <div style={{ fontSize: 16, color: brand.c.navy }}>
                {prescription.prescriber}
              </div>
              <div style={{ fontSize: 14, color: '#6B7280', marginTop: 4 }}>
                {prescription.prescriberPhone}
              </div>
            </div>
            
            {prescription.quantity && (
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#6B7280', marginBottom: 8 }}>
                  Quantity
                </div>
                <div style={{ fontSize: 16, color: brand.c.navy }}>
                  {prescription.quantity}
                </div>
              </div>
            )}
            
            {prescription.notes && (
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#6B7280', marginBottom: 8 }}>
                  Notes
                </div>
                <div style={{ fontSize: 16, color: brand.c.navy }}>
                  {prescription.notes}
                </div>
              </div>
            )}
          </div>
          
          {prescription.file && (
            <div style={{ marginTop: 24, paddingTop: 24, borderTop: '1px solid #E5E7EB' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#6B7280', marginBottom: 12 }}>
                Uploaded File
              </div>
              <div style={{
                border: '2px solid #E5E7EB',
                borderRadius: 8,
                overflow: 'hidden',
                maxWidth: 400
              }}>
                <img 
                  src={prescription.file} 
                  alt="Prescription"
                  style={{ width: '100%', display: 'block' }}
                />
              </div>
            </div>
          )}
        </Card>
        
        {/* Related Orders */}
        {relatedOrders.length > 0 && (
          <Card>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, color: brand.c.navy }}>
              Related Orders
            </h2>
            
            <div style={{ display: 'grid', gap: 12 }}>
              {relatedOrders.map(order => (
                <div
                  key={order.id}
                  onClick={() => onNavigate('order-detail', order.id)}
                  style={{
                    padding: 16,
                    background: '#F9FAFB',
                    borderRadius: 8,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#EFF6FF'}
                  onMouseLeave={e => e.currentTarget.style.background = '#F9FAFB'}
                >
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      Order {order.id}
                    </div>
                    <div style={{ fontSize: 14, color: '#6B7280' }}>
                      Placed {new Date(order.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Badge status={order.status} />
                    <Icon name="arrowRight" size={18} color="#9CA3AF" />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}